import { LoginPage } from "@presentation/pages";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Sidebar } from "@presentation/components/layout/sidebar/Sidebar"; // import {Sidebar} from '@presentation/components'; for some reason importing is nto workign this way with this component only...
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import { NotificationProvider } from "@presentation/components/notifications";
import React, { memo, useEffect, useState } from "react";
import "@utils/stringLocalization/i18n";
import OidcCallback from "./components/auth/OidcCallback";
import { useInjection } from "./components/dependencyInjection/useInjection";
import { AuthService } from "@infrastructure/services";
import { FullscreenLoader } from "@presentation/components/notifications";
import { logOutInProgress, signInProgress } from "@presentation/components/helpers/constants";
import { InactivityAutoSignOut } from "./components/auth";
import { UploadProgressIndicator } from "@presentation/components";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { appRoutes } from "@presentation/AppRoutes";

const MemoizedRoutes = memo(() => (
  <Routes>
    {appRoutes.map((route, index) => (
      <Route key={index} path={route.route} element={route.element} />
    ))}
  </Routes>
));

const App: React.FC = () => {
  const [checkedSession, setCheckedSession] = useState(false);
  const username = useUserManagementStore((state) => state.userSession?.username);
  const authService = useInjection(AuthService);

  useEffect(() => {
    const wasLoggedOut = sessionStorage.getItem(logOutInProgress) === "true";
    const wasSignInStarted = sessionStorage.getItem(signInProgress) === "true";
    if (wasLoggedOut || wasSignInStarted) {
      console.info("Skipping session restore");
      sessionStorage.removeItem(logOutInProgress); // Clear the flag so it doesn't persist
      setCheckedSession(true);
      return; // Skip session restore
    }
    console.info("Attempting to restore session");
    const tryRestoreSession = async () => {
      const session = useUserManagementStore.getState().userSession;
      const token = session?.accessToken ?? null;
      if (session.username) console.info("Token is present");

      if (token && !authService.isTokenExpired(token)) {
        console.info("Session restored from memory");
        setCheckedSession(true);
        return;
      }

      try {
        console.info("Attempting to refresh access token...");
        const refreshedToken = await authService.refreshAccessToken();

        if (refreshedToken) {
          console.info("Access token refreshed successfully");
          setCheckedSession(true);
          return;
        }

        throw new Error("No token returned from refresh");
      } catch (refreshError) {
        console.warn("Token refresh failed, falling back to sign-in:", refreshError);

        try {
          await authService.signIn();
          console.info("Session restored via sign-in");
        } catch (signInError) {
          console.warn("Sign-in failed:", signInError);
          useUserManagementStore.getState().clear();
        } finally {
          setCheckedSession(true);
        }
      }
    };

    tryRestoreSession();
  }, [authService]);

  if (!checkedSession) {
    return <FullscreenLoader />;
  }

  return (
    <NotificationProvider>
      {/* Tooltip provider: must be placed in App.tsx to enable tooltips across the app.
      It automatically shows a tooltip for any child element with a 'title' attribute.
      'parentTitle={true}' ensures the tooltip also works when the 'title' is on a parent element,
      which is especially useful for icon-only buttons. */}
      <Tooltip position="top" parentTitle={true} />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {!username ? (
          <Routes>
            <Route path="*" element={<LoginPage />} />
            <Route path="/auth/callback" element={<OidcCallback />} />
          </Routes>
        ) : (
          <>
            {checkedSession && <InactivityAutoSignOut />}
            <Sidebar routes={appRoutes}>
              <MemoizedRoutes />
              <UploadProgressIndicator />
            </Sidebar>
          </>
        )}
      </BrowserRouter>
    </NotificationProvider>
  );
};

export default App;
